import { AxiosResponse } from "axios";
import { pickBy } from "lodash";
import { ApiServiceParams } from "store/services";
import { IStore } from "store/store-context";
import { Covenant } from "store/types/deal/covanents";
import { convertDateRange, toFormData } from "utils";
import downloadFileAxios from "utils/download-file-axios";

import {
  AltiusRes,
  BankStatementUploadResult,
  ChartType,
  CrunchbaseData,
  DateRangeValue,
  DealBase,
  DealChunks,
  DealsQueries,
  DealsTable,
  IAssetClassMapping,
  IAxis,
  ICharacteristicViewData,
  ICreateDealData,
  ICreateInvestmentDetailData,
  ICryptocurrencyOption,
  IDeal,
  IDealAIExtractResponse,
  IDealCryptocurrency,
  IDealImportValidateResponse,
  IDealModule,
  IDealPatchData,
  IDealPreview,
  IDealRoundsWithInvestors,
  IDealsTableRow,
  IDealsWithInvestmentDetails,
  IDispersionBucket,
  IFile,
  IFolder,
  IGetDealsData,
  IGetInvestmentDetailsChart,
  IGetInvestmentDetailsChartResponse,
  IImportCsvResult,
  IInvestmentDetailCellValue,
  IInvestmentDetailField,
  IInvestmentDetailHistoryValue,
  IInvestmentDetailTableData,
  IMeasureMapping,
  IPaginationResponse,
  ISaveDealAsTemplateData,
  ISearchItem,
  ISecurityTableRow,
  IStatus,
  ITemplate,
  ITransformedChartConfig,
  IUpdateCapitalSeekerData,
  IUpdateInvestmentDetailData,
  IUpdateInvestmentDetailHistoryData,
  IWorkspaceMetadata,
  ReportFilters,
  SecurityTransaction,
  TableData,
  WorkspaceFiltersFromDB
} from "../../types";
import { DealAction, DocumentsAction, UserAction } from "../actions";
import { AsyncActionsParams } from "../base";
import { RootDealActions } from "./root-deal-actions";

export interface IDealActions {
  getDealsCardsAction: (data: IGetDealsData) => Promise<IPaginationResponse<IDealPreview>>;
  getDealAction: (id: number) => Promise<IDeal>;
  getDealAndStoreAction: (id: number) => Promise<void>;
  getDealRoundsWithInvestorsAndStoreAction: (id: number) => Promise<void>;
  createDealAction: (data: ICreateDealData) => Promise<{ dealId: number; dealTitle: string }>;
  downloadDealImportTemplateAction: () => Promise<void>;
  validateDealImportFileAction: (file: File) => Promise<IDealImportValidateResponse>;
  extractDealFromFileAction: (file: File) => Promise<IDealAIExtractResponse>;
  createDealFromFileAction: (data: {
    dealName: string;
    asset_class_name: string;
    characteristics: Array<{ name: string; value: string }>;
  }) => Promise<{ dealId: number; dealTitle: string }>;
  deleteDealAction: (id: number) => Promise<number>;
  updateDealAction: (data: IDealPatchData) => Promise<IDeal>;
  updateCapitalSeekerAction: (data: IUpdateCapitalSeekerData) => Promise<void>;
  getDealStatusesAction: () => Promise<void>;
  exportDealAction: (dealId: number) => Promise<void>;
  downloadUnsavedFilesAction: (dealId: number) => Promise<void>;
  exportDealActivityAction: (dealId: number) => Promise<void>;
  dealInvalidateAction: () => void;
  updateDealStatusAction: (data: { dealId: number } & IStatus) => Promise<void>;
  getDealsTableActions: (data: Partial<IGetDealsData>) => Promise<DealsTable>;
  downloadDealsListFileAction: (data: Partial<IGetDealsData>) => Promise<void>;
  getDealsBaseAction: (data?: Partial<IGetDealsData>) => Promise<DealBase[]>;
  getNewsMetaAction: (dealId: number, sectionId: number) => Promise<void>;
  invalidateNewsMetaAction: () => void;

  updateQueries: (queries: Partial<DealsQueries>, isFullReplace?: boolean) => void;

  getTemplatesAction: (with_folders?: boolean) => Promise<ITemplate[]>;
  updateTemplateAction: (data: { templateId: number; title?: string; is_ai?: boolean }) => Promise<ITemplate>;
  deleteTemplateAction: (templateId: number) => Promise<void>;
  createTemplateFromDealAction: (data: ISaveDealAsTemplateData) => Promise<string>;

  uploadLogoAction: (data: {
    dealId: number;
    file: File;
    onUploadProgress: ApiServiceParams["onUploadProgress"];
    signal?: AbortSignal;
  }) => Promise<void>;
  saveLogoAction: (data: { dealId: number; fileId: number }) => Promise<void>;
  setLogoToSaveAction: (data: IFile | null) => void;
  deleteLogoAction: (data: { logoId: number; isCurrentLogo?: boolean }) => Promise<void>;

  setHierarchyPanelOpenAction: (open: boolean) => void;
  updateDealHierarchyAction: (updateData: {
    dealId: number;
    parent?: number | null;
    subAssets?: number[];
  }) => Promise<void>;

  getDealChunksAction: (dealId: number) => Promise<void>;

  createCrucnbaseAction: (data: { dealId: number; crunchbase_link: string }) => Promise<CrunchbaseData>;
  getCrucnbaseAction: (dealId: number) => Promise<CrunchbaseData>;

  getDealsFiltersAction: () => Promise<ReportFilters>;
  getDealWorkspaceFiltersAction: (dealId: number) => Promise<WorkspaceFiltersFromDB>;
  getDealCharacteristicsAction: (dealId: number) => Promise<ICharacteristicViewData>;
  deleteSharedDealAction: (id: number) => Promise<void>;
  copySharedDealAction: (id: number) => Promise<number>;
  searchCryptocurrencyAction: (search: string) => Promise<ICryptocurrencyOption[]>;
  addDealCryptocurrencyAction: (coin_id: string, deal_id: number) => Promise<any>;
  getDealCryptocurrencyAction: (dealId: number) => Promise<IDealCryptocurrency>;

  getDealsTableRowsAction: (data?: Partial<IGetDealsData>) => Promise<IDealsTableRow[]>;
  getDealsTableColumnsActions: () => Promise<{ key: string; title: string }[]>;
  updateTableColumnSettingsAction: (data: string[]) => Promise<void>;
  updateDealNullifyCommitmentsAction: (data: { dealId: number; nullify: boolean }) => Promise<void>;

  getRatingHistoryAction: (dealId: number) => Promise<TableData>;
  createRatingHistoryAction: (data: {
    deal_id: number;
    rating_date: string;
    provider: string;
    value: string;
  }) => Promise<void>;

  advancedSearchAction: (searchValue: string) => Promise<ISearchItem[]>;
  setDealsQueryCubic: (data: {
    cubicKey: string;
    filter: string;
    title: string;
    indexName?: { index: string; name: string };
  }) => void;
  setInvestorsViewChart: (data: {
    key: string;
    measure?: string;
    groupBy?: string;
    chartType?: ChartType;
    xAxis?: IAxis;
    yAxis?: IAxis;
  }) => void;

  getInvestmentDetailsFieldsAction: () => Promise<IInvestmentDetailField[]>;
  getInvestmentValuesAction: (search: string, investmentId: number) => Promise<string[]>;
  updateInvestmentDetailAction: (data: IUpdateInvestmentDetailData) => Promise<void>;
  deleteInvestmentDetailAction: (dealId: number, uuid: string) => Promise<string>;
  getInvestmentDetailsAction: (dealId: number) => Promise<IInvestmentDetailTableData>;
  createInvestmentDetailAction: (data: ICreateInvestmentDetailData) => Promise<void>;
  getInvestmentDetailsHistoryDataAction: (
    dealId: number,
    dealInvestmentId: number
  ) => Promise<IInvestmentDetailHistoryValue[]>;
  saveInvestmentDetailDataAction: (data: IUpdateInvestmentDetailHistoryData) => Promise<IInvestmentDetailCellValue>;
  getInvestmentDetailsChartAction: (data: IGetInvestmentDetailsChart) => Promise<IGetInvestmentDetailsChartResponse>;
  exportInvestmentDetailsTableAction: (dealId: number) => Promise<void>;
  importInvestmentDetailsCsvAction: (dealId: number, file: File) => Promise<IImportCsvResult>;
  setInvestmentDetailsChartAction: (data: ITransformedChartConfig) => void;
  createInvestmentDetailFromAIAction: (data: { dealId: number; file_ids?: number[]; files?: File[] }) => Promise<void>;
  updateDealCharacteristicsFromInvestmentDetailsAction: (
    dealId: number,
    data: Record<string, Record<string, number>>
  ) => Promise<void>;
  getDispersionChartAction: (dealId: number) => Promise<IDispersionBucket[]>;
  getDealsWithInvestmentDetailsAction: () => Promise<IDealsWithInvestmentDetails[]>;

  getSecurityTableAction: (data?: {
    filters: ReportFilters;
    dateRange: DateRangeValue;
  }) => Promise<ISecurityTableRow[]>;
  updateSecurityConsolidationAction: (data: {
    dealId: number;
    securityId: number;
    roundName?: string;
  }) => Promise<void>;
  undoSecurityConsolidationAction: (securityId: number) => Promise<void>;
  getSecurityTransactionsAction: (securityId: number) => Promise<SecurityTransaction[]>;
  getDealTransactionsAction: (dealId: number) => Promise<SecurityTransaction[]>;
  uploadSecurityDataAction: (file: File) => Promise<void>;
  getSecuritiesMeasuresMapAction: () => Promise<IMeasureMapping[]>;
  saveSecuritiesMeasuresMapAction: (mappings: IMeasureMapping[]) => Promise<void>;
  getAssetClassMappingsAction: () => Promise<IAssetClassMapping[]>;
  saveAssetClassMappingsAction: (mappings: IAssetClassMapping[]) => Promise<void>;
  placeOnMarketAction: (payload: {
    file_ids: number[];
    files: File[];
    share_option: string[];
    dealId: number;
  }) => Promise<void>;

  getDealCovenantsAction: (dealId: number) => Promise<Covenant[]>;
  updateCovenantAction: (data: Partial<Covenant>) => Promise<Covenant>;
  createCovenantAction: (data: Omit<Covenant, "id" | "graph">) => Promise<Covenant>;
  deleteCovenantAction: (dealId: number, covId: number) => Promise<void>;

  // ai select the deal that matches the file name
  getDealFromFileNameAction: (data: { file_name: string }) => Promise<DealBase | null>;

  importBankStatementsAction: (data: { type: string; files: File[] }) => Promise<BankStatementUploadResult>;
}

export class DealActions extends RootDealActions implements IDealActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  // ===================================== Deal ======================================
  public getDealsCardsAction = async (requestParams: IGetDealsData): Promise<IPaginationResponse<IDealPreview>> => {
    const { data } = await this.api.post<IPaginationResponse<IDealPreview>>({
      url: "/api/v0.0.2/deals-cards",
      // params: {
      //   status_id: statusId,
      //   asset_class_id: assetClassId,
      //   ...rest
      // }
      data: { ...requestParams }
    });
    return data;
  };

  public getDealAction: IDealActions["getDealAction"] = async (id): Promise<IDeal> => {
    let url = "";
    const isSharedDeal = await this.isSharedDeal(id);
    if (isSharedDeal) {
      url = `/api/v0.0.3/shared-deals/${id}`;
    } else {
      url = `/api/v0.0.2/deals/${id}`;
    }
    const { data } = await this.api.get<IDeal>({ url });
    return data;
  };

  public getDealAndStoreAction: IDealActions["getDealAndStoreAction"] = async (data): Promise<void> => {
    const payload = await this.getDealAction(data);
    this.dispatch({ type: DealAction.GetDealByIdSuccess, payload });
  };

  public getDealRoundsWithInvestorsAndStoreAction: IDealActions["getDealRoundsWithInvestorsAndStoreAction"] = async (
    id
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IDealRoundsWithInvestors[]>>({
      url: `/api/v0.0.3/deals/${id}/round-investors`
    });
    this.dispatch({ type: DealAction.GetDealRoundsWithInvestorsSuccess, payload: data });
  };

  public createDealAction: IDealActions["createDealAction"] = async (dataToSend) => {
    const { file, characteristic_data, deal_capital_seeker, ...rest } = dataToSend;

    let data: Omit<ICreateDealData, "file"> | FormData = dataToSend;
    if (file) {
      data = toFormData({
        ...rest,
        ...(deal_capital_seeker?.length && { deal_capital_seeker: JSON.stringify(deal_capital_seeker) }),
        ...(characteristic_data?.length && { characteristic_data: JSON.stringify(characteristic_data) }),
        file
      });
    }

    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<IDeal>>({
      url: "/api/v0.0.2/deals",
      data,
      ...(file && { headers: { "Content-Type": "multipart/form-data" } })
    });
    this.dispatch({ type: UserAction.IncrementNumberOfDeals, payload: null });
    return { dealId: payload.id, dealTitle: payload.title };
  };

  public downloadDealImportTemplateAction = async (): Promise<void> => {
    return this.api
      .get({
        url: "/api/v0.0.3/deals/import-from-file/template",
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public validateDealImportFileAction = async (file: File): Promise<IDealImportValidateResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    const {
      data: { data }
    } = await this.api.post<{ data: IDealImportValidateResponse }>({
      url: "/api/v0.0.3/deals/import-from-file/validate",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
    return data;
  };

  public extractDealFromFileAction = async (file: File): Promise<IDealAIExtractResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    const {
      data: { data }
    } = await this.api.post<{ data: IDealAIExtractResponse }>({
      url: "/api/v0.0.3/deals/ai/extract-from-file",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
    return data;
  };

  public createDealFromFileAction = async (data: {
    dealName: string;
    asset_class_name: string;
    characteristics: Array<{ name: string; value: string }>;
  }): Promise<{ dealId: number; dealTitle: string }> => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<{ dealId: number; dealTitle: string }>>({
      url: "/api/v0.0.3/deals/import-from-file",
      data
    });
    this.dispatch({ type: UserAction.IncrementNumberOfDeals, payload: null });
    return { dealId: payload.dealId, dealTitle: payload.dealTitle };
  };

  public deleteDealAction = async (id: number): Promise<number> => {
    await this.api.delete({ url: `/api/v0.0.2/deals/${id}` });
    this.dispatch({ type: UserAction.DecrementNumberOfDeals, payload: null });
    return id;
  };

  public getDealStatusesAction = async (): Promise<void> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<IStatus[]>>({
      url: "/api/v0.0.2/deals/statuses/all"
    });
    this.dispatch({ type: DealAction.GetDealStatusesSuccess, payload });
  };

  public exportDealAction = async (dealId: number): Promise<void> => {
    return this.api
      .get({
        url: `/api/v0.0.2/deals/${dealId}/export`,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public downloadUnsavedFilesAction = async (dealId: number): Promise<void> => {
    return this.api
      .get({
        url: `/api/v0.0.2/deals/${dealId}/downloadUnsavedFiles`,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public exportDealActivityAction = async (dealId: number): Promise<void> => {
    return this.api
      .get({
        url: `/api/v0.0.3/deals/${dealId}/activities-export`,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public updateDealAction = async (data: IDealPatchData): Promise<IDeal> => {
    const { dealId, ...body } = data;
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<IDeal>>({
      url: `/api/v0.0.2/deals/${dealId}`,
      data: body
    });
    if (!payload.confirm_change) {
      this.dispatch({
        type: DealAction.UpdateDealSuccess,
        payload: payload
      });
    }
    return payload;
  };

  public updateCapitalSeekerAction = async ({ dealId, ...body }: IUpdateCapitalSeekerData): Promise<void> => {
    const {
      data: {
        data: { deal_capital_seeker }
      }
    } = await this.api.patch<AltiusRes<IDeal>>({
      url: `/api/v0.0.2/deals/${dealId}`,
      data: {
        deal_capital_seeker: [body]
      }
    });
    this.dispatch({
      type: DealAction.UpdateCapitalSeekerSuccess,
      payload: deal_capital_seeker
    });
  };

  public dealInvalidateAction = (): void => {
    this.dispatch({ type: DealAction.DealInvalidate, payload: null });
  };

  public updateDealStatusAction: IDealActions["updateDealStatusAction"] = async (data) => {
    const { dealId, id } = data;
    await this.api.post({
      url: `/api/v0.0.2/deals/${dealId}/status`,
      data: {
        status_id: id
      }
    });
    this.dispatch({
      type: DealAction.UpdateDealStatusSuccess,
      payload: data
    });
  };

  public getDealsTableActions: IDealActions["getDealsTableActions"] = async ({
    filters,
    deal_title,
    order_by,
    columns
  }) => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DealsTable>>({
      url: "/api/v0.0.2/deals-list",
      data: { filters: filtersPayload, deal_title, order_by, view: "table", columns }
    });

    return data;
  };

  public downloadDealsListFileAction: IDealActions["downloadDealsListFileAction"] = async ({
    filters,
    deal_title,
    order_by,
    columns
  }) => {
    const filtersPayload = pickBy(filters, (value) => value.length > 0);
    return this.api
      .post({
        url: "/api/v0.0.2/deals-list/export",
        data: { filters: filtersPayload, deal_title, order_by, view: "table", columns }
      })
      .then(downloadFileAxios);
  };

  public getDealsTableRowsAction: IDealActions["getDealsTableRowsAction"] = async (body) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IDealsTableRow[]>>({
      url: "/api/v0.0.2/deals-list",
      data: body
    });

    return data;
  };

  public getDealsBaseAction: IDealActions["getDealsBaseAction"] = async (body) => {
    const filtersPayload = body?.filters ? pickBy(body.filters, (value) => value.length > 0) : null;
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DealBase[]>>({
      url: "/api/v0.0.2/deals-list",
      data: { ...body, ...(filtersPayload && { filters: filtersPayload }), view: "task-manage" }
    });

    return data;
  };

  public getNewsMetaAction = async (dealId: number, folderId: number): Promise<void> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<IWorkspaceMetadata>>({
      url: `/api/v0.0.3/deals/${dealId}/folders/${folderId}`
    });
    this.dispatch({ type: DealAction.GetNewsMetaSuccess, payload });
  };

  public invalidateNewsMetaAction = (): void => {
    this.dispatch({ type: DealAction.InvalidateEmailMeta, payload: null });
  };

  public updateQueries = (payload: Partial<DealsQueries>, isFullReplace = false) => {
    this.dispatch({
      type: DealAction.UpdateQueries,
      payload: { queries: payload, isFullReplace }
    });
  };

  public getTemplatesAction = async (with_folders?: boolean): Promise<ITemplate[]> => {
    const {
      data: {
        data: { data }
      }
    } = await this.api.get<AltiusRes<IPaginationResponse<ITemplate>>>({
      url: "/api/v0.0.2/templates",
      params: { with_folders }
    });
    return data;
  };

  public updateTemplateAction: IDealActions["updateTemplateAction"] = async (data) => {
    const { templateId, ...body } = data;
    const {
      data: { data: response }
    } = await this.api.patch<AltiusRes<ITemplate>>({
      url: `/api/v0.0.2/templates/${templateId}`,
      data: body
    });
    return response;
  };

  public deleteTemplateAction: IDealActions["deleteTemplateAction"] = async (templateId) => {
    await this.api.delete({ url: `/api/v0.0.2/templates/${templateId}` });
  };

  public createTemplateFromDealAction = async (data: ISaveDealAsTemplateData): Promise<string> => {
    const { dealId, ...body } = data;
    await this.api.post({
      url: `/api/v0.0.2/deals/${dealId}/template`,
      data: body
    });
    return body.title;
  };

  public uploadLogoAction: IDealActions["uploadLogoAction"] = async ({ dealId, file, ...rest }): Promise<void> => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IFile>>({
      url: `/api/v0.0.3/deals/${dealId}/logo`,
      data: toFormData({
        logo: file
      }),
      headers: {
        "Content-Type": "multipart/form-data"
      },
      ...rest
    });
    this.setLogoToSaveAction(data);
  };

  public saveLogoAction: IDealActions["saveLogoAction"] = async ({ dealId, fileId }): Promise<void> => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IFile>>({
      url: `/api/v0.0.3/deals/${dealId}/logo/${fileId}`
    });
    this.setLogoToSaveAction(null);
    this.dispatch({
      type: DealAction.SaveLogoSuccess,
      payload: data
    });
  };

  public setLogoToSaveAction = (data: IFile | null): void => {
    this.dispatch({
      type: DealAction.SetLogoToSave,
      payload: data
    });
  };

  public deleteLogoAction: IDealActions["deleteLogoAction"] = async (data): Promise<void> => {
    const { logoId, isCurrentLogo } = data;
    await this.api.delete({ url: `/api/v0.0.3/deals/logo/${logoId}` });
    if (isCurrentLogo) {
      this.dispatch({
        type: DealAction.DeleteLogoSuccess,
        payload: null
      });
    } else {
      this.setLogoToSaveAction(null);
    }
  };

  public updateDealHierarchyAction: IDealActions["updateDealHierarchyAction"] = async ({
    dealId,
    parent,
    subAssets
  }) => {
    await this.api.patch({
      url: `/api/v0.0.3/deals/${dealId}/sub-assets`,
      data: {
        parent_id: parent,
        sub_assets: subAssets
      }
    });
  };

  public setHierarchyPanelOpenAction = (open: boolean): void => {
    this.dispatch({
      type: DealAction.SetHierarchyPanelOpen,
      payload: open
    });
  };

  public getDealChunksAction: IDealActions["getDealChunksAction"] = async (dealId) => {
    const isSharedDeal = await this.isSharedDeal(dealId);

    const [
      {
        data: { data: folders }
      },
      {
        data: { data: forms }
      },
      {
        data: { data: files }
      },
      {
        data: { data: documents }
      },
      {
        data: { data: notes }
      },
      {
        data: { data: emails }
      }
    ] = await Promise.all([
      this.api.get<AltiusRes<IDealModule["folders"]>>({
        url: isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/folders` : `/api/v0.0.3/deals/${dealId}/folders`
      }),
      this.api.get<AltiusRes<IDealModule["forms"]>>({
        url: isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/forms` : `/api/v0.0.3/deals/${dealId}/forms`
      }),
      this.api.get<AltiusRes<IDealModule["files"]>>({
        url: isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/files` : `/api/v0.0.3/deals/${dealId}/files`
      }),
      this.api.get<AltiusRes<IStore["documents"]>>({
        url: isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/documents` : "/api/v0.0.3/documents",
        params: { deal_id: dealId }
      }),
      this.api.get<AltiusRes<IDealModule["notes"]>>({
        url: isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/notes` : `/api/v0.0.3/deals/${dealId}/notes`
      }),
      this.api.get<AltiusRes<IDealModule["emails"]>>({
        url: isSharedDeal
          ? `/api/v0.0.3/shared-deals/${dealId}/incoming-emails`
          : `/api/v0.0.3/deals/${dealId}/incoming-emails`
      })
    ]);

    const foldersWithDummies: Record<number, IFolder> = { ...folders };

    const dealChunks: DealChunks = {
      folders: foldersWithDummies,
      forms,
      files,
      documents,
      notes,
      emails
    };

    this.dispatch({
      type: DocumentsAction.GetDocumentsSuccess,
      payload: documents
    });

    this.dispatch({
      type: DealAction.GetDealChunksSuccess,
      payload: dealChunks
    });
  };

  public createCrucnbaseAction: IDealActions["createCrucnbaseAction"] = async ({ dealId, crunchbase_link }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<CrunchbaseData>>({
      url: `/api/v0.0.3/deals/${dealId}/crunchbase/fetch`,
      data: { crunchbase_link }
    });
    this.dispatch({ type: DealAction.CreateCrunchbaseSuccess, payload: data.id });
    return data;
  };

  public getCrucnbaseAction: IDealActions["getCrucnbaseAction"] = async (dealId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<CrunchbaseData>>({ url: `/api/v0.0.3/deals/${dealId}/crunchbase` });
    return data;
  };

  public getDealsFiltersAction = async (): Promise<ReportFilters> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ReportFilters>>({
      url: "api/v0.0.3/deals-filters"
    });
    this.dispatch({
      type: DealAction.GetDealFiltersSuccess,
      payload: data
    });
    return data;
  };

  public getDealWorkspaceFiltersAction = async (dealId: number): Promise<WorkspaceFiltersFromDB> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<WorkspaceFiltersFromDB>>({
      url: `api/v0.0.3/deals/${dealId}/workspace-filters`
    });
    return data;
  };

  public getDealCharacteristicsAction = async (dealId: number): Promise<ICharacteristicViewData> => {
    const isSharedDeal = await this.isSharedDeal(dealId);
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<ICharacteristicViewData>>({
      url: isSharedDeal
        ? `/api/v0.0.3/shared-deals/${dealId}/deal-characteristics`
        : "/api/v0.0.3/deal-characteristics",
      params: {
        deal_id: dealId
      }
    });
    return payload;
  };

  public deleteSharedDealAction: IDealActions["deleteSharedDealAction"] = async (id) => {
    await this.api.delete({ url: `/api/v0.0.3/shared-deals/${id}` });
  };

  public copySharedDealAction: IDealActions["copySharedDealAction"] = async (id) => {
    const {
      data: {
        data: { deal_id }
      }
    } = await this.api.post<AltiusRes<{ deal_id: number }>>({ url: `/api/v0.0.3/shared-deals/${id}/copy-deal` });

    return deal_id;
  };

  public searchCryptocurrencyAction: IDealActions["searchCryptocurrencyAction"] = async (
    search
  ): Promise<ICryptocurrencyOption[]> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ICryptocurrencyOption[]>>({
      url: "api/v0.0.3/crypto/search",
      params: { search }
    });
    return data;
  };

  public addDealCryptocurrencyAction: IDealActions["addDealCryptocurrencyAction"] = async (coin_id, deal_id) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ crypto_coin_icon: string }>>({
      url: "api/v0.0.3/crypto/add",
      data: { coin_id, deal_id }
    });
    this.dispatch({
      type: DealAction.UpdateDealCryptoCoinIcon,
      payload: data.crypto_coin_icon
    });
  };

  public getDealCryptocurrencyAction: IDealActions["getDealCryptocurrencyAction"] = async (
    dealId
  ): Promise<IDealCryptocurrency> => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IDealCryptocurrency>>({
      url: `api/v0.0.3/crypto/${dealId}/get`
    });
    return data;
  };

  public getDealsTableColumnsActions: IDealActions["getDealsTableColumnsActions"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<{ key: string; title: string }[]>>({ url: "/api/v0.0.3/deals-list-columns" });
    return data;
  };

  public updateTableColumnSettingsAction: IDealActions["updateTableColumnSettingsAction"] = async (data) => {
    await this.api.post<AltiusRes<string[]>>({
      url: "/api/v0.0.3/user/settings",
      data: { type: "dealsTableColumns", data }
    });
    this.dispatch({ type: DealAction.UpdateQueries, payload: { queries: { tableColumns: data } } });
  };

  public updateDealNullifyCommitmentsAction: IDealActions["updateDealNullifyCommitmentsAction"] = async ({
    dealId,
    nullify
  }) => {
    this.api.post({ url: `api/v0.0.3/deals/${dealId}/unfunded-commitment`, data: { nullify } });
    this.dispatch({ type: DealAction.UpdateDealNullifyCommitment, payload: nullify });
  };

  public getRatingHistoryAction: IDealActions["getRatingHistoryAction"] = async (dealId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Awaited<ReturnType<IDealActions["getRatingHistoryAction"]>>>>({
      url: `/api/v0.0.3/deals/${dealId}/rating`
    });

    return data;
  };

  public createRatingHistoryAction: IDealActions["createRatingHistoryAction"] = async ({ deal_id, ...body }) => {
    await this.api.post<AltiusRes<Awaited<ReturnType<IDealActions["getRatingHistoryAction"]>>>>({
      url: `/api/v0.0.3/deals/${deal_id}/rating`,
      data: body
    });
  };

  public advancedSearchAction: IDealActions["advancedSearchAction"] = async (searchValue) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ISearchItem[]>>({
      url: `/api/v0.0.3/deals-advanced-search?search=${encodeURIComponent(searchValue)}`
    });

    return data;
  };

  public setDealsQueryCubic: IDealActions["setDealsQueryCubic"] = (payload) => {
    this.dispatch({ type: DealAction.SetDealsQueryCubic, payload });
  };

  public setInvestorsViewChart: IDealActions["setInvestorsViewChart"] = (payload) => {
    this.dispatch({ type: DealAction.SetInvestorsViewChart, payload });
  };

  public getInvestmentDetailsFieldsAction: IDealActions["getInvestmentDetailsFieldsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IInvestmentDetailField[]>>({ url: "/api/v0.0.3/investment-details/columns" });
    return data;
  };

  public createInvestmentDetailAction: IDealActions["createInvestmentDetailAction"] = async (data) => {
    const { dealId, values } = data;
    await this.api.post({ url: `/api/v0.0.3/investment-details/deals/${dealId}`, data: { data: values } });
  };

  public getInvestmentValuesAction: IDealActions["getInvestmentValuesAction"] = async (search, investmentId) => {
    const {
      data: {
        data: { values }
      }
    }: AxiosResponse<AltiusRes<{ values: string[] }>> = await this.api.get({
      url: `/api/v0.0.3/investment-details/${investmentId}/values`,
      params: { search }
    });
    return values;
  };

  public deleteInvestmentDetailAction: IDealActions["deleteInvestmentDetailAction"] = async (dealId, uuid) => {
    await this.api.delete({ url: `/api/v0.0.3/investment-details/deals/${dealId}`, data: { uuid } });
    return uuid;
  };

  public updateInvestmentDetailAction: IDealActions["updateInvestmentDetailAction"] = async (data) => {
    const {
      dealId,
      values: { id, ...values }
    } = data;

    await this.api.patch({ url: `/api/v0.0.3/investment-details/deals/${dealId}/data/${id}`, data: { data: values } });
  };

  public getInvestmentDetailsAction: IDealActions["getInvestmentDetailsAction"] = async (dealId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IInvestmentDetailTableData>>({
      url: `/api/v0.0.3/investment-details/deals/${dealId}`
    });
    return data;
  };

  public getInvestmentDetailsHistoryDataAction: IDealActions["getInvestmentDetailsHistoryDataAction"] = async (
    dealId,
    dealInvestmentId
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IInvestmentDetailHistoryValue[]>>({
      url: `/api/v0.0.3/investment-details/deals/${dealId}/data/${dealInvestmentId}/history`
    });
    return data;
  };

  public saveInvestmentDetailDataAction: IDealActions["saveInvestmentDetailDataAction"] = async (data) => {
    const { dealId, ...rest } = data;
    const {
      data: { data: cellData }
    } = await this.api.post<AltiusRes<IInvestmentDetailCellValue>>({
      url: `/api/v0.0.3/investment-details/deals/${dealId}/bulk-data`,
      data: { ...rest }
    });
    return cellData;
  };

  public exportInvestmentDetailsTableAction: IDealActions["exportInvestmentDetailsTableAction"] = async (dealId) => {
    return this.api
      .get({ url: `/api/v0.0.3/investment-details/deals/${dealId}/export`, responseType: "blob" })
      .then(downloadFileAxios);
  };

  public importInvestmentDetailsCsvAction: IDealActions["importInvestmentDetailsCsvAction"] = async (dealId, file) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IImportCsvResult>>({
      url: `/api/v0.0.3/investment-details/deals/${dealId}/import-csv`,
      headers: { "Content-Type": "multipart/form-data" },
      data: toFormData({ file, clear_missing_data: 1 })
    });
    return data;
  };

  public getInvestmentDetailsChartAction: IDealActions["getInvestmentDetailsChartAction"] = async (params) => {
    const { dealId, chartName, groupBy, valueBy } = params;
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IGetInvestmentDetailsChartResponse>>({
      url: `api/v0.0.3/investment-details/deals/${dealId}/charts`,
      params: {
        chart_name: chartName,
        group_by: groupBy,
        value_by: valueBy
      }
    });
    return data;
  };

  public getDispersionChartAction: IDealActions["getDispersionChartAction"] = async (dealId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IDispersionBucket[]>>({
      url: `api/v0.0.3/investment-details/deals/${dealId}/dispersion`
    });
    return data;
  };

  public createInvestmentDetailFromAIAction: IDealActions["createInvestmentDetailFromAIAction"] = async (data) => {
    const { dealId, file_ids, files } = data;
    const updatedData = { file_ids, files };
    const res = Object.fromEntries(Object.entries(updatedData).filter(([, value]) => value?.length));
    await this.api.post({
      url: `/api/v0.0.3/investment-details/deals/${dealId}/ask-ai`,
      headers: {
        "Content-Type": "multipart/form-data"
      },
      data: toFormData({ ...res })
    });
  };

  public updateDealCharacteristicsFromInvestmentDetailsAction: IDealActions["updateDealCharacteristicsFromInvestmentDetailsAction"] =
    async (dealId, data) => {
      await this.api.patch<AltiusRes<void>>({
        url: `/api/v0.0.3/investment-details/deals/${dealId}/update-deal-characteristics`,
        data
      });
    };

  public setInvestmentDetailsChartAction: IDealActions["setInvestmentDetailsChartAction"] = (payload) => {
    this.dispatch({ type: DealAction.SetInvestmentDetailsChart, payload });
  };

  public getDealsWithInvestmentDetailsAction: IDealActions["getDealsWithInvestmentDetailsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IDealsWithInvestmentDetails[]>>({
      url: "/api/v0.0.3/investment-details/deals-with-investment-data"
    });
    return data;
  };
  public getSecurityTableAction: IDealActions["getSecurityTableAction"] = async (data) => {
    const { filters, dateRange } = data || {};
    const {
      data: { data: res }
    } = await this.api.post<AltiusRes<ISecurityTableRow[]>>({
      //it must be post not get, because we added a filters to request
      url: "/api/v0.0.3/securities/list",
      data: {
        filters,
        dateRange: dateRange && convertDateRange(dateRange)
      }
    });
    return res;
  };

  public updateSecurityConsolidationAction: IDealActions["updateSecurityConsolidationAction"] = async (data) => {
    const { dealId, securityId, roundName } = data;
    await this.api.patch<AltiusRes<void>>({
      url: `/api/v0.0.3/securities/${securityId}/consolidate`,
      data: { deal_id: dealId, ...(roundName ? { round_name: roundName } : {}) }
    });
  };

  public undoSecurityConsolidationAction: IDealActions["undoSecurityConsolidationAction"] = async (securityId) => {
    await this.api.post<AltiusRes<void>>({
      url: `/api/v0.0.3/securities/${securityId}/consolidate/undo`
    });
  };

  public getSecurityTransactionsAction: IDealActions["getSecurityTransactionsAction"] = async (securityId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<SecurityTransaction[]>>({
      url: `/api/v0.0.3/securities/${securityId}/list`
    });
    return data;
  };

  public getDealTransactionsAction: IDealActions["getDealTransactionsAction"] = async (dealId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<SecurityTransaction[]>>({
      url: `/api/v0.0.3/securities/deal/${dealId}/list`
    });
    return data;
  };

  public getSecuritiesMeasuresMapAction: IDealActions["getSecuritiesMeasuresMapAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IMeasureMapping[]>>({
      url: "/api/v0.0.3/securities/measure-mapping"
    });
    return data;
  };

  public saveSecuritiesMeasuresMapAction: IDealActions["saveSecuritiesMeasuresMapAction"] = async (mappings) => {
    await this.api.post<AltiusRes<void>>({
      url: "/api/v0.0.3/securities/measure-mapping",
      data: mappings
    });
  };

  public getAssetClassMappingsAction: IDealActions["getAssetClassMappingsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IAssetClassMapping[]>>({
      url: "/api/v0.0.3/securities/asset-class-mapping"
    });
    return data;
  };

  public saveAssetClassMappingsAction: IDealActions["saveAssetClassMappingsAction"] = async (mappings) => {
    await this.api.post<AltiusRes<void>>({
      url: "/api/v0.0.3/securities/asset-class-mapping",
      data: mappings
    });
  };

  public uploadSecurityDataAction: IDealActions["uploadSecurityDataAction"] = async (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_type", "securities");

    await this.api.post<AltiusRes<void>>({
      url: "/api/v0.0.3/securities/upload-data",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  };

  public placeOnMarketAction: IDealActions["placeOnMarketAction"] = async (payload) => {
    const { dealId } = payload;
    const toFormDataPayload = {
      ...Object.fromEntries(Object.entries(payload).filter(([, value]) => Array.isArray(value) && value.length > 0))
    };

    await this.api.post<AltiusRes<void>>({
      url: `/api/v0.0.3/deals/${dealId}/place-on-market`,
      data: toFormData(toFormDataPayload),
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  };

  public getDealCovenantsAction: IDealActions["getDealCovenantsAction"] = async (dealId) => {
    const { data } = await this.api.get<AltiusRes<Covenant[]>>({
      url: `/api/v0.0.3/deals/${dealId}/covanents`
    });
    return data.data;
  };

  public updateCovenantAction = async (data: Partial<Covenant>) => {
    const { data: response } = await this.api.patch<AltiusRes<Covenant>>({
      url: `/api/v0.0.3/deals/${data.deal_id}/covanents/${data.id}`,
      data
    });
    return response.data;
  };

  public createCovenantAction = async (data: Omit<Covenant, "id" | "graph">) => {
    const { data: response } = await this.api.post<AltiusRes<Covenant>>({
      url: `/api/v0.0.3/deals/${data.deal_id}/covanents`,
      data
    });
    return response.data;
  };

  public deleteCovenantAction = async (dealId: number, covId: number) => {
    await this.api.delete<AltiusRes<void>>({
      url: `/api/v0.0.3/deals/${dealId}/covanents/${covId}`
    });
  };

  public importBankStatementsAction: IDealActions["importBankStatementsAction"] = async ({ type, files }) => {
    const formData = new FormData();
    formData.append("type", type);
    files.forEach((f) => formData.append("files[]", f));

    const { data: response } = await this.api.post<AltiusRes<BankStatementUploadResult>>({
      url: "/api/v0.0.3/securities/import-bank-statements",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
    return response.data;
  };

  public getDealFromFileNameAction: IDealActions["getDealFromFileNameAction"] = async ({ file_name }) => {
    const {
      data: {
        data: { deal }
      }
    } = await this.api.get<AltiusRes<{ deal: DealBase | null }>>({
      url: "/api/v0.0.3/deals/ai/suggested",
      params: { file_name }
    });
    return deal;
  };
}
